import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICheckSettlementPeriodsParams,
  ICheckSettlementPeriodsResponse,
  ICreateLabel,
  IEditLabel,
  IGetLabelListInfiniteParams,
  ILabelPageResponse,
  ILabelResponse,
} from './types';

export const useGetLabelListInfinite = (
  params: IGetLabelListInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ILabelResponse[], DefaultError>({
    queryKey: ['labels', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ILabelPageResponse>(response, {
        groupName: `useGetLabelListInfinite - ${pageParam}`,
      });

      return infiniteHandler<ILabelResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetLabel = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelResponse, DefaultError>({
    queryKey: ['label', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Label/${id}`,
      });

      return responseHandler(response, {
        groupName: `usetGetLabel - ${id}`,
        notFoundPath: '/label',
      });
    },
    enabled: enabled,
  });
};

export const usePostLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostLabel');
  return useMutation<ILabelResponse, DefaultError, ICreateLabel>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.labelName ?? i18next.t('label.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Label', body });

      return responseHandler(response, {
        groupName: 'usePostLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.labelName ?? i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutLabel');

  return useMutation<ILabelResponse, DefaultError, IEditLabel>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.labelName ?? i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Label',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.labelName ?? i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteLabel');

  return useMutation<void, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Label/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCheckSettlementPeriods = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<
    ICheckSettlementPeriodsResponse,
    DefaultError,
    ICheckSettlementPeriodsParams
  >({
    mutationFn: async params => {
      const response = await apiClient({
        method: 'GET',
        url: 'Settlement/Check',
        params: params,
      });

      return responseHandler(response, {
        groupName: 'useCheckSettlementPeriods',
      });
    },
  });
};

export const useGetAllVcfLabels = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Vcf/All',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.vcf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return responseHandler(response, {
        groupName: 'useGetAllVcfLabels',
      });
    },
  });
};

export const useGetVcfLabel = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<void, DefaultError, string>({
    mutationFn: async (labelId: string) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Vcf',
        params: {
          LabelId: labelId
        }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contact-${labelId}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return responseHandler(response, {
        groupName: 'useGetVcfLabel',
      });
    },
  });
};
